import { Controller, useFormContext } from 'react-hook-form'

import { Box, Button, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import withCustomizationFields, {
  WithCustomizationFieldsProps,
  isMandatory,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import {
  FieldTypes,
  Tax,
  TaxesLimit,
  fieldType,
} from 'modules/expenses/hooks/useExpensesCustomization'

import TaxFields from './TaxFields'
import { shouldShowBlock } from './form/schema'

const FIELD_NAME = 'taxes'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {}

const Taxes = (props: Props) => {
  const { customizationFields, resourceType, documentType, isFieldReadOnly } = props
  const { control, setValue, watch } = useFormContext()

  const taxesFieldType = fieldType(customizationFields, resourceType, 'taxes') ?? 'tax_type'
  const taxes = watch('taxes')
  const currency = watch('currency')

  const onAddTax = () => {
    setValue('taxes', [...taxes, { id: uuid() }])
  }

  const onDeleteTax = (index: number) => {
    const updatedTaxes = taxes.filter((tax: Tax, i: number) => i !== index)
    setValue('taxes', updatedTaxes, { shouldDirty: true, shouldValidate: true })
  }

  const isTaxesMandatory = isMandatory('taxes', resourceType, customizationFields, documentType)
  const taxesAreEmpty = taxes.length === 0
  const hasAnyValueTax = taxes.some(
    //yes, we want to check for null and undefined
    (tax: Tax) => tax.base_amount != null || tax.percentage != null || tax.amount != null
  )

  useUnsafeEffect(() => {
    // if taxes are mandatory and empty, display an empty tax field
    if (isTaxesMandatory && taxesAreEmpty) {
      setValue('taxes', [{ id: uuid() }])
    }
  }, [isTaxesMandatory, taxesAreEmpty, setValue])

  useUnsafeEffect(() => {
    // if taxes are not mandatory and have no values, hide the taxes
    if (!isTaxesMandatory && !hasAnyValueTax) {
      setValue('taxes', [], { shouldDirty: true, shouldValidate: true })
    }
  }, [isTaxesMandatory, hasAnyValueTax, setValue])

  const borderStyle =
    taxes.length > 0
      ? ({ all: { width: 's2', color: 'grey400', style: 'solid' } } as const)
      : undefined
  const paddingStyle = taxes.length > 0 ? ('s16' as const) : ('s0' as const)

  const showAddButton =
    !isFieldReadOnly && taxes.length < TaxesLimit && taxesFieldType === FieldTypes.tax_type

  const showBlock = shouldShowBlock(customizationFields, resourceType, documentType)
  if (!showBlock) return null

  return (
    <Box border={borderStyle} padding={paddingStyle} gap="s16" borderRadius={{ all: 'abs008' }}>
      <Controller
        name={FIELD_NAME}
        control={control}
        render={({ field }) => (
          <>
            {taxes.length > 0 && (
              <>
                <Box gap="s24" gapDivider width="full">
                  {taxes.map((tax: Tax, index: number) => (
                    <TaxFields
                      {...props}
                      {...field}
                      key={tax.id}
                      tax={tax}
                      index={index}
                      currency={currency}
                      taxFieldType={taxesFieldType}
                      onDelete={() => onDeleteTax(index)}
                      isFieldMandatory={isTaxesMandatory}
                      isFieldReadOnly={isFieldReadOnly ?? false}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      />
      {showAddButton && (
        <Box alignItems="flexStart">
          <Button type="secondary" onClick={onAddTax} iconName="add">
            {i18n.t('expenses_v2.forms.fields.taxes.add_tax_breakdown')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
export default withCustomizationFields(withFieldPermissions(Taxes, FIELD_NAME))
